import { Injectable, inject } from '@angular/core';
import type { StepParams } from '@enlighten/ts-shared/step-api';
import { FileService } from '../file/file.service';
import { CacheService } from '../cache/cache.service';
import { FileDataService } from '../file-data/file-data.service';
import { GeneralHelpers } from '../../helpers/general.helper';
import {
  FILE_EXTENSIONS,
  FILE_TYPE,
  RELOAD_FILES,
  ResponseType,
} from '../../constants/general.constants';
import { MessageService } from '../message/message.service';
import { firstValueFrom } from 'rxjs';
import { NotebookFilesService } from '../notebook-files/notebook-files.service';
import { ExecutionContext } from '../../interfaces/chunk/chunk-context.interface';
import { AppStateService } from '../app-state/app-state.service';
import { NotebookDefaultFilesService } from '../notebook-default-file/notebook-default-file.service';

/**
 * Unified file service for accessing files from multiple sources
 *
 * This service provides a unified interface for accessing files from:
 * 1. Project files (version-specific)
 * 2. Notebook default files (shared across all versions - stored in head version 0.0)
 * 3. Cache files (version-specific)
 *
 * IMPORTANT: When searching for files, this service checks default files, which are
 * ALWAYS visible across all versions because they're stored in the head version (0.0).
 * This ensures that when users switch between versions, default files remain accessible.
 */
@Injectable({
  providedIn: 'root',
})
export class FileUnifiedService {
  private fileService = inject(FileService);
  private cacheService = inject(CacheService);
  private fileDataService = inject(FileDataService);
  private notebookFilesService = inject(NotebookFilesService);
  private notebookDefaultFilesService = inject(NotebookDefaultFilesService);
  private appState = inject(AppStateService);
  private messageService = inject(MessageService);


  private fileTypeProcessingMap = new Map<FILE_TYPE, string>([
    [FILE_TYPE.video, 'bytes'],
    [FILE_TYPE.audio, 'bytes'],
    [FILE_TYPE.image, 'bytes'],
    [FILE_TYPE.parquet, 'bytes'],
    [FILE_TYPE.document, 'bytes'],  // xlsx, xls - Office documents are ZIP archives
    [FILE_TYPE.pdf, 'bytes'],      // PDF files are binary data
    [FILE_TYPE.csv, 'bytes'],
    [FILE_TYPE.json, 'default'],
    [FILE_TYPE.arrow, 'default'],
    [FILE_TYPE.text, 'default'],
    [FILE_TYPE.javascript, 'default'],
    [FILE_TYPE.python, 'default'],
    [FILE_TYPE.md, 'default'],
    [FILE_TYPE.wasm, 'bytes'],
    [FILE_TYPE.whl, 'bytes'],
    [FILE_TYPE.blob, 'bytes'],
    [FILE_TYPE.binary, 'bytes'],
    [FILE_TYPE.unknown, 'default'],
  ]);

  private getFileProcessingMethod(fileType: FILE_TYPE): string {
    return this.fileTypeProcessingMap.get(fileType) || 'default';
  }

  /**
   * Searches for a file in project files and returns content if found
   * ✅ OPTIMIZED: Uses cached fileList instead of calling get() again
   */
  private async searchProjectFiles(
    fileName: string,
    responseType: string,
    fileType: FILE_TYPE
  ): Promise<any | null> {
    try {
      // Use cached fileList if available, otherwise fetch
      let projectFiles: any[] = this.fileService.fileList;

      // If fileList is empty, fetch it (this will use cache if available)
      if (!projectFiles || projectFiles.length === 0) {
        projectFiles = await firstValueFrom(this.fileService.get());
      }

      const projectFile = GeneralHelpers.findElementInArrayByProp(
        projectFiles,
        'name',
        fileName
      );

      if (projectFile) {
        const fileId = projectFile['fileId'];

        try {
          const file = await firstValueFrom(
            this.fileService.getById(fileId, responseType as any)
          );
          return await this.getFileContent(file, fileName, responseType, fileType);
        } catch (error) {
          console.warn('Error fetching from project files:', error);
        }
      }
    } catch (error) {
      console.warn('Error getting project files list:', error);
    }

    return null;
  }

  /**
   * Searches for a file in notebook default files and returns content if found
   */
  private async searchDefaultFiles(
    fileName: string,
    responseType: string,
    fileType: FILE_TYPE
  ): Promise<any | null> {
    try {
      const projectId = this.appState.projectId();
      const workflowId = this.appState.workflowId();
      if (projectId && workflowId) {
        const defaultFiles = await firstValueFrom(
          this.notebookDefaultFilesService.getDefaultFiles(projectId, workflowId)
        );
        const defaultFile = GeneralHelpers.findElementInArrayByProp(
          defaultFiles as any,
          'name',
          fileName
        );

        if (defaultFile) {
          const fileId = defaultFile['fileId'] ? String(defaultFile['fileId']) : null;
          try {
            const file = await firstValueFrom(
              this.notebookDefaultFilesService.getDefaultFileContents(
                projectId,
                workflowId,
                fileId,
                responseType as 'blob' | 'json'
              )
            );
            return await this.getFileContent(file, fileName, responseType, fileType);
          } catch (error) {
            console.warn('Error fetching from default files:', error);
          }
        }
      }
    } catch (error) {
      console.warn('Error getting default files list:', error);
    }
    return null;
  }

  /**
   * Searches for a file in notebook cache files and returns content if found
   */
  private async searchCacheFiles(
    fileName: string,
    responseType: string,
    fileType: FILE_TYPE
  ): Promise<any | null> {
    try {
      const notebookFiles: any[] = await firstValueFrom(
        this.cacheService.getCacheContent()
      );
      const notebookFile = GeneralHelpers.findElementInArrayByProp(
        notebookFiles,
        'name',
        fileName
      );
      if (notebookFile) {
        const fileId = notebookFile.fileId;
        try {
          const file = await firstValueFrom(
            this.cacheService.getCacheContentFile(fileId, responseType as any)
          );
          return await this.getFileContent(file, fileName, responseType, fileType);
        } catch (error) {
          console.warn('Error fetching from cache:', error);
        }
      }
    } catch (error) {
      console.warn('Error getting cache files list:', error);
    }
    return null;
  }

  private async getFileContent(
    file: any,
    fileName: string,
    responseType: string,
    fileType: FILE_TYPE
  ): Promise<any> {
    if (file === null || file === undefined) {
      return null;
    }

    const processingMethod = this.getFileProcessingMethod(fileType);

    if (processingMethod === 'bytes') {
      switch (responseType) {
        case ResponseType.ArrayBuffer: {
          // If file is a Blob, convert to ArrayBuffer first
          if (file instanceof Blob) {
            const arrayBuffer = await file.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);
            return uint8Array;
          }
          const uint8Array = new Uint8Array(file);
          return uint8Array;
        }
        case ResponseType.Text:
        case ResponseType.Blob: {
          // For JavaScript context compatibility, convert Blob to Uint8Array
          // This prevents the '[object Blob]' serialization issue
          if (file instanceof Blob) {
            const arrayBuffer = await file.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);
            return uint8Array;
          }
          return file;
        }
        default:
          return file;
      }
    }

    const fileExtension = GeneralHelpers.fileExtensionFromString(fileName);

    if (
      fileExtension === FILE_EXTENSIONS.csv ||
      fileExtension === FILE_EXTENSIONS.arrow
    ) {
      return await this.fileDataService.getFileCallback(file, fileExtension);
    }

    return file;
  }

  /**
   * `EHQ.listFiles` from a JavaScript step: the names `getFile` can read,
   * sorted. The union of the cache, default and project files, each name once,
   * as enoch's tiers list them (EHQ-525). It takes no arguments, so `_params`
   * is unused. The Python bridge has its own enoch-shaped listing.
   */
  public async listFiles(_params?: StepParams['EHQ.listFiles']): Promise<string[]> {
    try {
      const [workflowFiles, projectFiles] = await Promise.all([
        this.notebookFilesService.getNotebookFiles(),
        this.listProjectFiles(),
      ]);
      const names = new Set([...workflowFiles, ...projectFiles].map((file) => file.name));
      return [...names].sort((a, b) => a.localeCompare(b));
    } catch (error) {
      console.error('Error listing workflow files:', error);
      return [];
    }
  }

  /** The version's project files; none when they can't be listed. */
  private async listProjectFiles(): Promise<Array<{ name: string }>> {
    try {
      const loaded = this.fileService.fileList;
      return loaded?.length ? loaded : ((await firstValueFrom(this.fileService.get())) ?? []);
    } catch (error) {
      console.warn('Error listing project files:', error);
      return [];
    }
  }

  /**
   * `EHQ.getFile` from a step (EHQ-10). The parser has validated the name and
   * stripped any `files/`/`default/`/`project/` prefix.
   *
   * @param source the step runtime ('javascript' or 'python')
   */
  public getFile(
    params: StepParams['EHQ.getFile'],
    chunkContext?: ExecutionContext,
    source?: string
  ): Promise<any> {
    return this.readFile(params.fileName, params.responseType, chunkContext, source);
  }

  /**
   * Reads a file by name from cache, default and project files.
   *
   * @param responseType omitted picks one from the file extension
   * @param source the step runtime ('javascript' or 'python') when a step is
   *   reading: a step reads its own cache files before the workflow's default
   *   files (SCRUM-428). Other callers omit it.
   */
  public async readFile(
    fileName: string,
    responseType?: string,
    chunkContext?: ExecutionContext,
    source?: string
  ): Promise<any> {
    try {
      if (typeof fileName !== 'string' || !fileName.trim()) {
        const error = new Error('Invalid or missing file name.');
        if (chunkContext) {
          chunkContext.addMessage('Invalid or missing file name.', 'danger');
        }
        throw error;
      }

      const fileExtension = GeneralHelpers.fileExtensionFromString(fileName);
      const resolvedType = responseType || this.fileDataService.getResponseType(fileExtension);
      const fileType = GeneralHelpers.getFileType(fileExtension);

      // A step reads as it would headless (enoch's jobReadDirs): its own cache
      // files (SCRUM-428 routes step `saveFile` there), then the workflow's
      // default files, then project files; the first match wins (EHQ-525).
      // Non-step callers (UI/external) keep project-first.
      const isFromStepExecution = source === 'python' || source === 'javascript';
      // An empty file is found: only null/undefined means "not in this tier".
      const file = isFromStepExecution
        ? (await this.searchCacheFiles(fileName, resolvedType, fileType as FILE_TYPE)) ??
          (await this.searchDefaultFiles(fileName, resolvedType, fileType as FILE_TYPE)) ??
          (await this.searchProjectFiles(fileName, resolvedType, fileType as FILE_TYPE))
        : (await this.searchProjectFiles(fileName, resolvedType, fileType as FILE_TYPE)) ??
          (await this.searchDefaultFiles(fileName, resolvedType, fileType as FILE_TYPE)) ??
          (await this.searchCacheFiles(fileName, resolvedType, fileType as FILE_TYPE));

      if (file !== null && file !== undefined) {
        return file;
      }

      // If we get here, the file wasn't found in any location
      const error = new Error(`File '${fileName}' not found in any location.`);
      if (chunkContext) {
        chunkContext.addMessage(`File '${fileName}' not found in any location.`, 'danger');
      }
      throw error;
    } catch (error: any) {
      if (chunkContext) {
        chunkContext.addMessage(`Error fetching file: ${error.message || error}`, 'danger');
      }
      throw error;
    }
  }

  /**
   * Notify UI listeners that a file was removed so lists refresh without a page reload.
   */
  private notifyFileRemoved(source: 'project' | 'notebook'): void {
    if (source === 'project') {
      this.messageService.sendMessage(RELOAD_FILES);
      return;
    }

    this.notebookFilesService.clearCache();
  }

  /**
   * Delete a file by name and return which list it came from.
   */
  private async removeFileFromAnyLocation(
    fileName: string,
    chunkContext?: ExecutionContext
  ): Promise<'project' | 'notebook'> {
    const errors: string[] = [];

    // Try to delete from project files
    try {
      let projectFiles: any[] = this.fileService.fileList;

      if (!projectFiles || projectFiles.length === 0) {
        projectFiles = await firstValueFrom(this.fileService.get());
      }

      const projectFile = GeneralHelpers.findElementInArrayByProp(
        projectFiles,
        'name',
        fileName
      );

      if (projectFile) {
        const fileId = projectFile['fileId'];
        await firstValueFrom(this.fileService.delete(fileId));
        if (chunkContext) {
          chunkContext.addMessage(`File '${fileName}' deleted from project files.`, 'success');
        }
        return 'project';
      }
    } catch (error: any) {
      errors.push(`Project files: ${error.message}`);
    }

    // Try to delete from notebook default files
    try {
      const projectId = this.appState.projectId();
      const workflowId = this.appState.workflowId();
      if (projectId && workflowId) {
        const defaultFiles = await firstValueFrom(
          this.notebookDefaultFilesService.getDefaultFiles(projectId, workflowId)
        );
        const defaultFile = GeneralHelpers.findElementInArrayByProp(
          defaultFiles as any,
          'name',
          fileName
        );

        if (defaultFile) {
          const fileId = defaultFile['fileId'] ? String(defaultFile['fileId']) : null;
          if (fileId) {
            await this.notebookFilesService.deleteFile(fileId, true);
            if (chunkContext) {
              chunkContext.addMessage(`File '${fileName}' deleted from default files.`, 'success');
            }
            return 'notebook';
          }
        }
      }
    } catch (error: any) {
      errors.push(`Default files: ${error.message}`);
    }

    // Try to delete from notebook cache files
    try {
      const notebookFiles: any[] = await firstValueFrom(
        this.cacheService.getCacheContent()
      );
      const notebookFile = GeneralHelpers.findElementInArrayByProp(
        notebookFiles,
        'name',
        fileName
      );
      if (notebookFile) {
        const fileId = notebookFile.fileId;
        await firstValueFrom(this.cacheService.deleteCacheContentFile(fileId));
        if (chunkContext) {
          chunkContext.addMessage(`File '${fileName}' deleted from cache files.`, 'success');
        }
        return 'notebook';
      }
    } catch (error: any) {
      errors.push(`Cache files: ${error.message}`);
    }

    const errorMessage = `File '${fileName}' not found in any location. Locations checked: project files, default files, cache files.`;
    if (chunkContext) {
      chunkContext.addMessage(errorMessage, 'danger');
    }

    if (errors.length > 0) {
      console.warn('Errors during file search:', errors);
    }

    throw new Error(errorMessage);
  }

  /**
   * Delete one of the step's own files by name: a cache file, which is where a
   * step's `saveFile` writes (SCRUM-428). Never a project file or a workflow
   * default file. This is the browser side of enoch's `EHQ.deleteFile`, which
   * deletes workflow files only (EHQ-432).
   *
   * @returns false when no cache file has that name; the caller decides
   *   whether that is an error (it also checks Pyodide's FS copy).
   */
  public async deleteWorkflowFile(fileName: string, _chunkContext?: ExecutionContext): Promise<boolean> {
    const cacheFiles: any[] = await firstValueFrom(this.cacheService.getCacheContent());
    const file = GeneralHelpers.findElementInArrayByProp(cacheFiles ?? [], 'name', fileName);
    if (!file) {
      return false;
    }
    await firstValueFrom(this.cacheService.deleteCacheContentFile(file.fileId));
    this.notifyFileRemoved('notebook');
    return true;
  }

  /**
   * Remove a file by name from all possible locations
   * Searches in: project files, notebook default files and cache files
   * ✅ OPTIMIZED: Uses cached fileList instead of calling get() again
   *
   * Usage in Python:
   * ```python
   * await ehq.removeFile('example.csv')
   * ```
   *
   * Usage in JavaScript:
   * ```javascript
   * await ehq.removeFile('example.csv')
   * ```
   *
   * @param args Array containing the file name as first element
   * @param chunkContext Execution context for messages and error handling
   * @returns Promise that resolves when file is deleted
   * @throws Error if file name is invalid or file is not found in any location
   */
  public async removeFile(
    args: any[],
    chunkContext?: ExecutionContext,
    options?: { notifyUi?: boolean }
  ): Promise<void> {
    try {
      if (!args.length || typeof args[0] !== 'string' || !args[0].trim()) {
        const error = new Error('Invalid or missing file name.');
        if (chunkContext) {
          chunkContext.addMessage('Invalid or missing file name.', 'danger');
        }
        throw error;
      }

      const fileName = args[0].trim();
      const deletedFrom = await this.removeFileFromAnyLocation(fileName, chunkContext);

      if (options?.notifyUi !== false) {
        this.notifyFileRemoved(deletedFrom);
      }
    } catch (error: any) {
      if (chunkContext) {
        chunkContext.addMessage(`Error removing file: ${error.message || error}`, 'danger');
      }
      throw error;
    }
  }

  /**
   * Remove multiple files by names from all possible locations
   * Searches in: project files, notebook default files and cache files
   *
   * Usage in Python:
   * ```python
   * await ehq.removeFiles(['example1.csv', 'example2.json'])
   * ```
   *
   * Usage in JavaScript:
   * ```javascript
   * await ehq.removeFiles(['example1.csv', 'example2.json'])
   * ```
   *
   * @param args Array containing an array of file names as first element
   * @param chunkContext Execution context for messages and error handling
   * @returns Promise that resolves with deletion results
   */
  public async removeFiles(
    args: any[],
    chunkContext?: ExecutionContext
  ): Promise<{ fileName: string; success: boolean; error?: string }[]> {
    try {
      if (!args.length || !Array.isArray(args[0]) || args[0].length === 0) {
        const error = new Error('Invalid or missing file names array.');
        if (chunkContext) {
          chunkContext.addMessage('Invalid or missing file names array.', 'danger');
        }
        throw error;
      }

      const fileNames: string[] = args[0];
      const results: { fileName: string; success: boolean; error?: string }[] = [];
      let deletedFromProject = false;
      let deletedFromNotebook = false;

      for (const fileName of fileNames) {
        try {
          const deletedFrom = await this.removeFileFromAnyLocation(
            fileName.trim(),
            chunkContext
          );
          results.push({ fileName, success: true });
          if (deletedFrom === 'project') {
            deletedFromProject = true;
          } else {
            deletedFromNotebook = true;
          }
        } catch (error: any) {
          results.push({
            fileName,
            success: false,
            error: error.message || 'Unknown error'
          });
        }
      }

      if (deletedFromProject) {
        this.notifyFileRemoved('project');
      }
      if (deletedFromNotebook) {
        this.notifyFileRemoved('notebook');
      }

      // Summary message
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      if (chunkContext) {
        if (successCount > 0) {
          chunkContext.addMessage(
            `Successfully deleted ${successCount} file${successCount > 1 ? 's' : ''}`,
            'success'
          );
        }
        if (failCount > 0) {
          chunkContext.addMessage(
            `Failed to delete ${failCount} file${failCount > 1 ? 's' : ''}`,
            'danger'
          );
        }
      }

      return results;
    } catch (error: any) {
      if (chunkContext) {
        chunkContext.addMessage(`Error removing files: ${error.message || error}`, 'danger');
      }
      throw error;
    }
  }
}
